import type { Dayjs } from 'dayjs';
import { z } from 'zod';

/**
 * Zod schema for the 訪問編集 (CheckinRemarksPage) form.
 *
 * Currently field-level rules cover only visit_time (required + HH:mm format).
 * Time-overlap with other visits on the same day is checked at submit time
 * inside the page (needs other visits as runtime data, not part of the form).
 */
export const massagerEditVisitFormSchema = z
  .object({
    visit_time: z.string().default(''),
    duration: z
      .number({
        required_error: '所要時間を正しく入力してください。',
        invalid_type_error: '所要時間を正しく入力してください。',
      })
      .nullable()
      .optional(),
    clinic_location_id: z.number().int().positive().optional(),
    is_not_hot_treatment: z.boolean().default(false),
    is_sudden: z.boolean().default(false),
    medical_institution_name: z.string().default(''),
    doctor_name: z.string().default(''),
    sudden_reason: z.string().default(''),
    coordination_date: z
      .custom<Dayjs | null>(v => v === null || v === undefined || typeof v === 'object', {
        message: '連携日を入力してください。',
      })
      .nullable()
      .default(null),
    remarks: z.string().default(''),
  })
  .superRefine((data, ctx) => {
    if (!/^\d{2}:\d{2}$/.test(data.visit_time)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['visit_time'],
        message: '日付を正しく入力してください。',
      });
    }

    if (data.clinic_location_id == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clinic_location_id'],
        message: '施設を選択してください。',
      });
    }

    // duration: required, integer, 1..600
    if (data.duration == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: '所要時間を正しく入力してください。',
      });
    } else if (!Number.isInteger(data.duration) || data.duration < 1 || data.duration > 600) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: '所要時間を正しく入力してください。',
      });
    }

    // Cross-day: visit_datetime_to (start + duration) must stay on the same date.
    const timeMatch = data.visit_time.match(/^(\d{2}):(\d{2})$/);
    if (timeMatch && typeof data.duration === 'number' && data.duration > 0) {
      const startMinutes = parseInt(timeMatch[1], 10) * 60 + parseInt(timeMatch[2], 10);
      if (startMinutes + data.duration > 1440) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['duration'],
          message: '日付をまたいだ施術日は登録できません',
        });
      }
    }

    // 突発的な往療 — when ticked, all 4 fields are required.
    if (!data.is_sudden) return;
    if (!data.medical_institution_name.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['medical_institution_name'],
        message: '医療機関名を入力してください。',
      });
    }
    if (!data.doctor_name.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['doctor_name'],
        message: '医師名を入力してください。',
      });
    }
    if (!data.sudden_reason.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sudden_reason'],
        message: '突発往療理由を入力してください。',
      });
    }
    if (!data.coordination_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['coordination_date'],
        message: '連携日を入力してください。',
      });
    }
  });

export type MassagerEditVisitFormData = z.infer<typeof massagerEditVisitFormSchema>;
